/* import __COLOCATED_TEMPLATE__ from './handoff-to-salesforce-case.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type HandoffToSalesforceCaseType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type HandoffToSalesforceTeamType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-team';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { isEmpty, isPresent } from '@ember/utils';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import { action } from '@ember/object';
import { BlocksDocument } from '@intercom/embercom-prosemirror-composer';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import { type Block } from '@intercom/interblocks.ts';
import type Store from '@ember-data/store';

export interface HandoffToSalesforceCaseArgs {
  step: HandoffToSalesforceCaseType | HandoffToSalesforceTeamType;
  editorState: EditorState;
  readOnly: boolean;
  isTemplatePreview?: boolean;
  settingsBannerHeading?: string;
}

export interface HandoffOption {
  text: string;
  value: string | number | null;
  isDisabled?: boolean;
}

export interface HandoffOptionGroup {
  heading: string;
  items: HandoffOption[];
}

export default class HandoffToSalesforceCase extends Component<HandoffToSalesforceCaseArgs> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isLoading(): boolean {
    return (
      this.finStandaloneService.salesforceDataLoader.isLoadingSalesforceSetupData ||
      this.finStandaloneService.salesforceDataLoader.isLoadingSalesforceConfig
    );
  }

  get salesforceCaseDescriptionSource() {
    if (this.isHandoffToSalesforceCase) {
      return (this.args.step as HandoffToSalesforceCaseType).salesforceCaseDescriptionSource;
    }

    return;
  }

  get isHandoffToSalesforceCase(): boolean {
    // we use this component for handoff to salesforce team as well
    return !!this.args.step.typeKey?.endsWith('handoff-to-salesforce-case');
  }

  get handoverOptions(): HandoffOptionGroup[] {
    let admins = this.salesforceSetupData.admins?.map((admin) => ({
      text: admin.name,
      value: admin.id,
    }));
    let queues = this.salesforceSetupData.queues?.map((queue) => ({
      text: queue.name,
      value: queue.id,
    }));

    let options = [];

    if (isPresent(queues)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.queues-title',
        ),
        items: queues,
      });
    }

    if (isPresent(admins)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.users-title',
        ),
        items: admins,
      });
    }

    if (isEmpty(options)) {
      return [this.noQueuesOrUsersPlaceholderGroupOption];
    } else if (this.isHandoffToSalesforceCase) {
      // case owner is optional for handoff to salesforce case
      return [this.unassignedGroupOption, ...options];
    } else {
      return options;
    }
  }

  private get noQueuesOrUsersPlaceholderGroupOption(): HandoffOptionGroup {
    return {
      heading: '',
      items: [
        {
          text: this.intl.t(
            'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.no-queues-or-users-available',
          ),
          value: '',
          isDisabled: true,
        },
      ],
    };
  }

  private get unassignedGroupOption(): HandoffOptionGroup {
    return {
      heading: '',
      items: [
        {
          text: this.intl.t(
            'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.unassigned-owner-label',
          ),
          value: null,
        },
      ],
    };
  }

  get selectedHandoverOption(): HandoffOption | undefined {
    let selectedHandoverOption = this.handoverOptions
      .flatMap((group) => group.items)
      .find((option) => option.value === this.args.step.handoffToSalesforceOwnerId);
    return selectedHandoverOption;
  }

  get settingsBannerHeading(): string {
    return (
      this.args.settingsBannerHeading ||
      this.intl.t(
        'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.banner-heading',
      )
    );
  }

  get salesforceCaseSubjectBlocks(): BlocksDocument {
    let blocks = this.caseSubjectBlocksForStep;

    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Case from Fin AI Agent with {{full_name}}',
        }),
      ]);
    }
    return blocks;
  }

  private get caseSubjectBlocksForStep() {
    if (this.isHandoffToSalesforceCase) {
      return new BlocksDocument(
        (this.args.step as HandoffToSalesforceCaseType).salesforceCaseSubjectBlocks?.toArray() ||
          [],
      );
    }

    return new BlocksDocument([]);
  }

  get salesforceCaseDescriptionBlocks(): BlocksDocument {
    let blocks = this.caseDescriptionBlocksForStep;

    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Created from Fin AI Agent',
        }),
      ]);
    }
    return blocks;
  }

  private get caseDescriptionBlocksForStep() {
    if (this.isHandoffToSalesforceCase) {
      return new BlocksDocument(
        (
          this.args.step as HandoffToSalesforceCaseType
        ).salesforceCaseDescriptionBlocks?.toArray() || [],
      );
    }

    return new BlocksDocument([]);
  }

  private shouldShowDefaultBlocks(blocks: BlocksDocument) {
    if (blocks.blocks.length === 0) {
      return true;
    }

    if (blocks.blocks.length === 1) {
      let block = blocks.blocks[0];
      return 'text' in block && typeof block.text === 'string' && block.text.trim() === '';
    }

    return false;
  }

  @action setHandoffToSalesforceOwnerId(handoffId: string) {
    this.args.step.handoffToSalesforceOwnerId = handoffId;
  }

  @action
  setCaseDescriptionSource(source: SalesforceCaseDescriptionSource) {
    if (this.isHandoffToSalesforceCase) {
      (this.args.step as HandoffToSalesforceCaseType).salesforceCaseDescriptionSource = source;
    }
  }

  @action
  onCaseSubjectChange(blockDocuments: BlocksDocument) {
    if (this.isHandoffToSalesforceCase) {
      let blockFragments = blockDocuments.blocks.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );

      let step = this.args.step as HandoffToSalesforceCaseType;

      step.salesforceCaseSubjectBlocks?.clear();
      step.salesforceCaseSubjectBlocks?.pushObjects(blockFragments);
    }
  }

  @action
  onCaseDescriptionChange(blockDocuments: BlocksDocument) {
    if (this.isHandoffToSalesforceCase) {
      let blockFragments = blockDocuments.blocks.map((block: Block) =>
        createFragmentFromBlock(this.store, block),
      );

      let step = this.args.step as HandoffToSalesforceCaseType;

      step.salesforceCaseDescriptionBlocks?.clear();
      step.salesforceCaseDescriptionBlocks?.pushObjects(blockFragments);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase': typeof HandoffToSalesforceCase;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case': typeof HandoffToSalesforceCase;
  }
}
